import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useAuth } from '../hooks/useAuth';
import { UserManagementRBAC } from '../components/UserManagementRBAC';
import { apiService } from '../services/api';
import { useOrganizationFeatureFlags } from '../stores/organizationFeatureFlagsStore';

interface OrganizationSettingsResponse {
  operationMode: 'standalone' | 'connected';
  emCodingAssistantEnabled?: boolean;
  cptCodingAssistantEnabled?: boolean;
  enableSpeakerAutoCorrect?: boolean;
}

export const Settings: React.FC = () => {
  const { user } = useAuth();
  const [activeSection, setActiveSection] = useState<'organization' | 'users'>('organization');

  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';

  // Clinical Coding Features state for admins editing their own org.
  const [emEnabled, setEmEnabled] = useState<boolean | null>(null);
  const [cptEnabled, setCptEnabled] = useState<boolean | null>(null);
  const [speakerAutoCorrectEnabled, setSpeakerAutoCorrectEnabled] = useState<boolean | null>(null);
  // Standalone practices do not set clinical coding or speaker auto-correct themselves; the
  // platform team does (AMP-148). The mode comes from the server with the settings.
  const [operationMode, setOperationMode] = useState<'standalone' | 'connected' | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const applyFlagsToStore = useOrganizationFeatureFlags((s) => s.applyFlags);

  // Fetch current org settings once the admin opens this page.
  useEffect(() => {
    if (!isAdmin || !user?.organizationId) return;
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        const res = await apiService.get<OrganizationSettingsResponse>('/v1/organizations/settings');
        if (cancelled) return;
        setEmEnabled(res?.emCodingAssistantEnabled ?? true);
        setCptEnabled(res?.cptCodingAssistantEnabled ?? true);
        setSpeakerAutoCorrectEnabled(res?.enableSpeakerAutoCorrect ?? true);
        setOperationMode(res?.operationMode === 'connected' ? 'connected' : 'standalone');
      } catch (err) {
        if (!cancelled) {
          toast.error('Failed to load organization settings');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [isAdmin, user?.organizationId]);

  const saveFlag = async (
    field: 'emCodingAssistantEnabled' | 'cptCodingAssistantEnabled' | 'enableSpeakerAutoCorrect',
    value: boolean
  ) => {
    try {
      setSaving(true);
      await apiService.put('/v1/organizations/settings', { [field]: value });
      // Reflect the change in the in-memory store so the clinical UI surfaces
      // react immediately without a full page reload.
      applyFlagsToStore({ [field]: value });
      if (field === 'emCodingAssistantEnabled') setEmEnabled(value);
      else if (field === 'cptCodingAssistantEnabled') setCptEnabled(value);
      else setSpeakerAutoCorrectEnabled(value);
      toast.success('Saved');
    } catch (err) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-8">
            Settings
          </h1>

          {/* Tab Navigation */}
          <div className="border-b border-gray-200 mb-8">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveSection('organization')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeSection === 'organization'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Organization
              </button>
              {isAdmin && (
                <button
                  onClick={() => setActiveSection('users')}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    activeSection === 'users'
                      ? 'border-indigo-500 text-indigo-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Users
                </button>
              )}
            </nav>
          </div>

          {/* Tab Content */}
          {activeSection === 'organization' && (
            <div>
              {isAdmin && user?.organizationId && operationMode === null ? (
                <p className="text-sm text-gray-500">Loading current settings…</p>
              ) : isAdmin && user?.organizationId && operationMode === 'connected' ? (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900 pb-3 border-b border-gray-200">
                      Clinical Coding Features
                    </h2>
                    <p className="text-sm text-gray-500 mt-2">
                      Control whether E/M and CPT sections appear in the Coding Assistant and in EHR deliveries for this organization. Diagnostic (ICD-10) codes always remain visible.
                    </p>
                  </div>

                  {loading || emEnabled === null || cptEnabled === null || speakerAutoCorrectEnabled === null ? (
                    <p className="text-sm text-gray-500">Loading current settings…</p>
                  ) : (
                    <div className="space-y-5">
                      <div className="flex items-center justify-between">
                        <div className="pr-4">
                          <label className="font-medium text-gray-900">EM Coding Assistant</label>
                          <p className="text-sm text-gray-500 mt-0.5">
                            Show the E/M visit code section in the coding assistant and the EHR delivery confirmation.
                          </p>
                        </div>
                        <button
                          type="button"
                          disabled={saving}
                          onClick={() => saveFlag('emCodingAssistantEnabled', !emEnabled)}
                          className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
                            emEnabled ? 'bg-indigo-600' : 'bg-gray-200'
                          }`}
                        >
                          <span
                            className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                              emEnabled ? 'translate-x-5' : 'translate-x-0'
                            }`}
                          />
                        </button>
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="pr-4">
                          <label className="font-medium text-gray-900">CPT Coding Assistant</label>
                          <p className="text-sm text-gray-500 mt-0.5">
                            Show the procedure CPT code section in the coding assistant, the EHR delivery confirmation, and the &ldquo;Improve for coding&rdquo; button on the treatment summary.
                          </p>
                        </div>
                        <button
                          type="button"
                          disabled={saving}
                          onClick={() => saveFlag('cptCodingAssistantEnabled', !cptEnabled)}
                          className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
                            cptEnabled ? 'bg-indigo-600' : 'bg-gray-200'
                          }`}
                        >
                          <span
                            className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                              cptEnabled ? 'translate-x-5' : 'translate-x-0'
                            }`}
                          />
                        </button>
                      </div>
                    </div>
                  )}

                  {!loading && speakerAutoCorrectEnabled !== null && (
                    <>
                      <div>
                        <h2 className="text-lg font-semibold text-gray-900 pb-3 border-b border-gray-200">
                          Speaker Attribution
                        </h2>
                        <p className="text-sm text-gray-500 mt-2">
                          High-confidence speaker corrections are applied automatically to the transcript when the system can verify them against multiple anchors (acoustic, role, conversation, clinical, chart). Lower-confidence cases are always surfaced for physician review.
                        </p>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="pr-4">
                          <label className="font-medium text-gray-900">Auto-correct speaker attributions</label>
                          <p className="text-sm text-gray-500 mt-0.5">
                            When enabled, high-confidence corrections are applied automatically (always reviewable, with a Revert button). When disabled, every correction is surfaced for manual review before any change.
                          </p>
                        </div>
                        <button
                          type="button"
                          disabled={saving}
                          onClick={() => saveFlag('enableSpeakerAutoCorrect', !speakerAutoCorrectEnabled)}
                          className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
                            speakerAutoCorrectEnabled ? 'bg-indigo-600' : 'bg-gray-200'
                          }`}
                        >
                          <span
                            className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                              speakerAutoCorrectEnabled ? 'translate-x-5' : 'translate-x-0'
                            }`}
                          />
                        </button>
                      </div>
                    </>
                  )}

                </div>
              ) : (
                <p className="text-gray-500">Organization settings are managed by your platform administrator.</p>
              )}
            </div>
          )}

          {/* User Management Section */}
          {activeSection === 'users' && (
            <UserManagementRBAC />
          )}
        </div>
      </div>
    </div>
  );
};
